import { useState } from 'react';
import { Building, Plus, ArrowLeft, Globe, Lock, Loader2 } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

interface CreateWorkspaceViewProps {
    onNavigate: (view: string) => void;
}

export default function CreateWorkspaceView({ onNavigate }: CreateWorkspaceViewProps) {
    const { createWorkspace, error: wsError } = useWorkspace();
    const [name, setName] = useState('');
    const [visibility, setVisibility] = useState<'private' | 'invite_only'>('private');
    const [includeCompany, setIncludeCompany] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [vatId, setVatId] = useState('');
    const [country, setCountry] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCreate = async () => {
        if (!name.trim()) {
            setError('Workspace name is required');
            return;
        }

        setIsCreating(true);
        setError(null);

        const result = await createWorkspace(name.trim(), {
            visibility,
            ...(includeCompany && companyName.trim() ? {
                company: {
                    name: companyName.trim(),
                    vatId: vatId.trim() || undefined,
                    country: country.trim() || undefined,
                },
            } : {}),
        });

        setIsCreating(false);

        if (result) {
            onNavigate('dashboard');
        } else {
            setError(wsError || 'Failed to create workspace. Please try again.');
        }
    };

    return (
        <div className="max-w-xl mx-auto py-8 animate-fade-in">
            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
                <button
                    onClick={() => onNavigate('dashboard')}
                    className="p-2 hover:bg-hig-bg-tertiary rounded-lg transition-colors text-hig-text-secondary"
                >
                    <ArrowLeft size={20} />
                </button>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <Plus size={20} className="text-white" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-hig-text-primary">Create Workspace</h1>
                    <p className="text-sm text-hig-text-secondary">Set up a new workspace to organize your replicas and team</p>
                </div>
            </div>

            <div className="space-y-6">
                {/* Workspace Name */}
                <div className="card p-6">
                    <label className="block text-sm font-semibold text-hig-text-primary mb-2">
                        Workspace Name
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="e.g. My Studio, Acme Corp"
                        className="w-full bg-hig-bg-tertiary border border-hig-border rounded-lg px-4 py-3 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-2 focus:ring-hig-accent/40"
                        autoFocus
                    />
                </div>

                {/* Visibility */}
                <div className="card p-6">
                    <label className="block text-sm font-semibold text-hig-text-primary mb-3">
                        Visibility
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => setVisibility('private')}
                            className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${visibility === 'private'
                                    ? 'border-hig-accent bg-hig-accent/5'
                                    : 'border-hig-border hover:border-hig-text-tertiary'
                                }`}
                        >
                            <Lock size={18} className={visibility === 'private' ? 'text-hig-accent' : 'text-hig-text-tertiary'} />
                            <div className="text-left">
                                <p className="text-sm font-medium text-hig-text-primary">Private</p>
                                <p className="text-xs text-hig-text-tertiary">Only invited members</p>
                            </div>
                        </button>
                        <button
                            onClick={() => setVisibility('invite_only')}
                            className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${visibility === 'invite_only'
                                    ? 'border-hig-accent bg-hig-accent/5'
                                    : 'border-hig-border hover:border-hig-text-tertiary'
                                }`}
                        >
                            <Globe size={18} className={visibility === 'invite_only' ? 'text-hig-accent' : 'text-hig-text-tertiary'} />
                            <div className="text-left">
                                <p className="text-sm font-medium text-hig-text-primary">Invite Only</p>
                                <p className="text-xs text-hig-text-tertiary">Discoverable by invite code</p>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Company (optional) */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-hig-text-primary flex items-center gap-2">
                            <Building size={16} className="text-hig-text-secondary" />
                            Link to Company
                        </label>
                        <button
                            onClick={() => setIncludeCompany(!includeCompany)}
                            className={`relative w-10 h-5 rounded-full transition-colors ${includeCompany ? 'bg-hig-accent' : 'bg-hig-bg-tertiary'}`}
                        >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${includeCompany ? 'left-5.5 translate-x-0.5' : 'left-0.5'}`} />
                        </button>
                    </div>
                    <p className="text-xs text-hig-text-tertiary mb-4">Optionally link this workspace to a legal entity for billing and compliance.</p>

                    {includeCompany && (
                        <div className="space-y-3 animate-fade-in">
                            <input
                                type="text"
                                value={companyName}
                                onChange={e => setCompanyName(e.target.value)}
                                placeholder="Company Name"
                                className="w-full bg-hig-bg-tertiary border border-hig-border rounded-lg px-4 py-2.5 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent/40"
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    value={vatId}
                                    onChange={e => setVatId(e.target.value)}
                                    placeholder="VAT ID (optional)"
                                    className="bg-hig-bg-tertiary border border-hig-border rounded-lg px-4 py-2.5 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent/40"
                                />
                                <input
                                    type="text"
                                    value={country}
                                    onChange={e => setCountry(e.target.value)}
                                    placeholder="Country Code (e.g. FR)"
                                    maxLength={2}
                                    className="bg-hig-bg-tertiary border border-hig-border rounded-lg px-4 py-2.5 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent/40"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-sm text-red-400">{error}</p>
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={() => onNavigate('dashboard')}
                        className="flex-1 py-3 bg-hig-bg-tertiary hover:bg-hig-bg-elevated text-hig-text-primary rounded-xl text-sm font-medium transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        disabled={isCreating || !name.trim()}
                        className="flex-1 py-3 bg-hig-accent hover:bg-hig-accent/90 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {isCreating ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                Creating…
                            </>
                        ) : (
                            <>
                                <Plus size={16} />
                                Create Workspace
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
